/*
Homepage v5 — reprise du design export "Homepage We Are Scene".
Sections : hero + recherche, bandeau de preuve, sélection de la semaine,
accompagnement expert (brief), hall of fame, vos projets, nos services,
témoignages, bloc hôte (simulateur de gains), CTA final.
*/
import { useEffect, useState } from 'react';
import { graphql } from 'react-relay';
import Image from 'next/image';
import { nanoid } from 'nanoid';
import { AnimatePresence } from 'framer-motion';
import Head from '@/core/head';
import { useTranslation } from '@/helpers/translate';
import YouTubeModal, { InstagramModal } from '@/core/videos';
import { getPathByLang, getRootUrl } from '@/helpers/router';
import { useZusUserConnectedStore } from '@/helpers/user-auth.js';
import {
  FadeUp, Stagger, StaggerChild, motion,
} from '@/core/motion';
import { FIDELITY_LEVELS } from '@/components/corpo/FidelityProgramCard.js';
import { tierIconsCompact } from '@/components/icons/hall-of-fame-tiers';
import HeroSearch from '@/components/home/heroSearch';
import HostSimulator from '@/components/home/hostSimulator';
import SelectionCard from '@/components/home/selectionCard';
import { useHomeCopy } from '@/components/home/homeCopy';
import {
  CARD, SECTION, ArrowIcon, SearchIcon, PlayIcon, Kicker, ProofBand,
} from '@/components/home/primitives';

const queryGQL = graphql`
  query homeQuery($tags: [String!]!, $lang: String!, $limit: Int) {
    spaces : getSpacesByTags(lang: $lang, tags: $tags, categories: ["production"], limit: $limit) {
      title(lang: $lang)
      type
      surface
      urlByLang(lang: $lang)
      location {
        city
        locality
      }
      pictures(count: 1) {
        nameId
        extension
        predictName
      }
      dataByCategory(category: "production") {
        capacities {
          capacity
        }
        price {
          amount_excl_tax
        }
        corpoPrice: price(input: {byViewer : true}) {
          amount_excl_tax
        }
      }
    }
  }
`;

const queryVariables = () => ({
  tags: ['home_type_loft'],
  limit: 6,
});

export {
  queryGQL as query,
  queryVariables,
};

export function getHead() {
  return {
    title: 'home:meta_title',
    description: 'home:meta_desc',
  };
}

const HOW_IT_WORKS_VIDEO_ID = 'YLp-Cwl_pvc';
// Témoignage d'hôte (chaîne YouTube We Are Scene) : « Vous disposez d'un lieu unique ? Devenez hôte ».
const HOST_VIDEO_ID = 'NaB8pyH0ZEk';
const HOF_TIER_KEYS = ['scout', 'blockbuster', 'legend'];
const HOF_TIER_STYLE = {
  scout: { bar: '33%' },
  blockbuster: { bar: '66%' },
  legend: { bar: '100%' },
};

const USAGE_IMAGES = {
  cinema: '/img/home-v5/usage-cinema.jpg',
  clip: '/img/home-v5/usage-clip.jpg',
  contenu: '/img/home-v5/usage-contenu.jpg',
  docu: '/img/home-v5/usage-docu.jpg',
  interview: '/img/home-v5/usage-interview.jpg',
  pub: '/img/home-v5/usage-pub.jpg',
  shooting: '/img/home-v5/usage-shooting.jpg',
  tv: '/img/home-v5/usage-tv.jpg',
};

const TESTIMONIAL_VIDEOS = {
  featured: 'https://www.instagram.com/p/DUEGqLjisdJ/',
  side: ['https://www.instagram.com/p/DKEOdObi7BY/', 'https://www.instagram.com/p/DLufXGQC0tr/'],
};
// Logo Moncher : blanc sur transparent, inversé pour les fonds clairs.
const FEATURED_LOGO = { src: '/img/home-v5/logo-moncher-crop.png', alt: 'Moncher Production', className: 'h-10 [filter:invert(1)]' };
const SIDE_LOGOS = [
  { src: '/img/home-v5/logo-mylittlebox-crop.png', alt: 'My Little Box', className: 'h-7' },
  { src: '/img/home-v5/logo-mlrecords-black-crop.png', alt: 'Moonlife Records', className: 'h-11' },
];

// Déclenche le flux « ajouter un lieu » du header (même mécanique que l'ancienne home).
function gotoAddSpace() {
  useZusUserConnectedStore.setState({ what: `clickonaddspacefromheaderaddspacefrombecomehost${nanoid()}` });
}

const HowItWorks = YouTubeModal(({ openModalYoutube, label }) => (
  <button
    type="button"
    onClick={openModalYoutube}
    className="mt-7 inline-flex items-center gap-2.5 rounded-lg border border-white/40 bg-white/10 px-5 py-3 text-[15px] font-normal text-white backdrop-blur-sm transition duration-200 hover:bg-white hover:text-was-ink"
  >
    <PlayIcon size={18} fill="currentColor" />
    {label}
  </button>
));

function useRotatingIndex(intervalMs) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setIdx((i) => i + 1), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return idx;
}

function HeroSection({ tx }) {
  const formats = tx('hero.formats') || [];
  return (
    <section className="was-grain relative flex min-h-[100svh] flex-col justify-center bg-was-night">
      <div className="absolute inset-0 overflow-hidden">
        <div className="was-hero-zoom absolute inset-0">
          <Image src="/img/home-v5/hero-bts.jpg" alt="" fill priority sizes="100vw" className="object-cover" />
        </div>
      </div>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(30,22,48,0.92)_0%,rgba(30,22,48,0.66)_45%,rgba(19,14,27,0.32)_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5 bg-[linear-gradient(180deg,rgba(19,14,27,0)_0%,rgba(19,14,27,0.55)_100%)]" />
      {/* Dégradé en tête pour la lisibilité du header transparent (logo, liens). */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-[linear-gradient(180deg,rgba(19,14,27,0.72)_0%,rgba(19,14,27,0.35)_55%,rgba(19,14,27,0)_100%)]" />
      {/* Bloc centré verticalement dans le fold ; pt pour le header fixe, pb pour la barre mobile fixe. */}
      <div className="relative mx-auto w-full max-w-[1240px] px-6 pb-28 pt-28 lg:px-8 lg:pb-16 lg:pt-32">
        <h1 className="was-fade-up m-0 max-w-[900px] text-[32px] font-heading font-extrabold uppercase tracking-[-0.01em] leading-[1.02] text-white md:text-[44px] lg:text-[54px]">{tx('hero.title')}</h1>
        <p className="was-fade-up mt-6 max-w-[720px] text-xl font-normal leading-[1.45] text-white [text-shadow:0_1px_2px_rgba(19,14,27,0.7),0_4px_24px_rgba(19,14,27,0.7)] md:text-[25px]" style={{ animationDelay: '90ms' }}>{tx('hero.subtitle')}</p>
        <div className="was-fade-up" style={{ animationDelay: '180ms' }}>
          <HowItWorks videoId={HOW_IT_WORKS_VIDEO_ID} label={tx('hero.howItWorks')} />
        </div>
        {/* z-20 : les menus (hubs, calendrier, mots-clés) doivent passer au-dessus des chips. */}
        <div className="was-fade-up relative z-10 mt-10 lg:mt-12" style={{ animationDelay: '260ms' }}>
          <HeroSearch tx={tx} />
        </div>
        <div className="was-fade-up relative z-[5]" style={{ animationDelay: '420ms' }}>
          <ul className="mt-4 hidden flex-wrap items-center gap-x-3.5 gap-y-1.5 pl-2 md:flex">
            {formats.map((label, i) => (
              <li key={`homeformat${label}`} className="inline-flex items-center gap-3.5 text-[13px] font-normal tracking-[0.02em] text-white/70">
                {label}
                {i < formats.length - 1 && <span className="size-[3px] rounded-full bg-white/40" />}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function SelectionSection({ tx, spaces, searchHref }) {
  if (!spaces?.length) return null;
  return (
    <section className="bg-was-cream">
      <div className={SECTION}>
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
          <FadeUp>
            <Kicker n="01" scene={tx('sceneLabel')}>{tx('selection.kicker')}</Kicker>
            <h2 className="mt-5 max-w-[900px] text-balance text-[26px] font-heading font-extrabold uppercase tracking-[-0.01em] leading-[1.06] md:text-[34px]">{tx('selection.title')}</h2>
          </FadeUp>
          <a href={searchHref} className="mb-1.5 inline-flex shrink-0 items-center gap-2 text-[15px] font-normal text-was-ink underline decoration-was-ink/30 underline-offset-4 transition-colors hover:decoration-was-ink">
            {tx('selection.all')}
            <ArrowIcon />
          </a>
        </div>
        <Stagger stagger={0.08} delay={0.1} className="mt-10 grid gap-5 md:grid-cols-2 lg:mt-12 lg:grid-cols-3">
          {spaces.map((s, i) => (
            <StaggerChild key={`homeselection${s.urlByLang || s.title}`} className={i === 0 ? 'h-full md:col-span-2 md:row-span-2' : 'h-full'}>
              <SelectionCard item={s} tx={tx} featured={i === 0} />
            </StaggerChild>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function BriefSection({ tx, briefHref }) {
  return (
    <section id="brief" className="border-t border-was-ink/8 bg-white">
      <div className="mx-auto grid max-w-[1240px] items-center gap-10 px-6 pt-20 lg:grid-cols-[1.25fr_0.75fr] lg:gap-16 lg:px-8 lg:pt-28">
        <FadeUp>
          <Kicker n="02" scene={tx('sceneLabel')}>{tx('brief.kicker')}</Kicker>
          <h2 className="mt-5 text-balance text-[26px] font-heading font-extrabold uppercase tracking-[-0.01em] leading-[1.06] md:text-[34px]">{tx('brief.title')}</h2>
          <p className="mt-5 max-w-[480px] text-base leading-relaxed text-was-slate">{tx('brief.desc')}</p>
          <a href={briefHref} className="mt-8 inline-flex items-center gap-2 rounded-lg bg-was-ink px-7 py-3.5 text-[15px] font-normal text-white transition duration-200 hover:bg-was-violet hover:text-white">
            {tx('brief.cta')}
            <ArrowIcon />
          </a>
        </FadeUp>
        <FadeUp delay={0.1} className={`relative aspect-[4/5] w-full overflow-hidden lg:aspect-auto lg:h-[520px] ${CARD}`}>
          <Image src="/img/home-v5/portrait-brief.jpg" alt={tx('brief.imageAlt')} fill sizes="(max-width: 1024px) 100vw, 480px" quality={88} className="object-cover object-[50%_22%]" />
        </FadeUp>
      </div>
      <div className="mx-auto max-w-[1240px] px-6 pb-20 pt-12 lg:px-8 lg:pb-28 lg:pt-16">
        <FadeUp className={`grid gap-5 bg-white px-6 py-7 lg:grid-cols-[56px_minmax(0,1fr)] lg:gap-7 lg:px-10 lg:py-9 ${CARD}`}>
          <span className="flex size-14 shrink-0 items-center justify-center rounded-full bg-was-ink text-white">
            <SearchIcon size={22} stroke="#ffffff" />
          </span>
          <div className="grid gap-x-12 gap-y-2 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-start">
            <h3 className="m-0 text-[22px] font-bold leading-[1.2] tracking-[-0.02em] text-was-ink lg:text-[24px]">{tx('brief.offTitle')}</h3>
            <p className="m-0 max-w-[62ch] text-[16px] leading-[1.6] text-was-slate">{tx('brief.offDesc')}</p>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function HallOfFameSection({ tx, t, hofHref }) {
  const tiers = HOF_TIER_KEYS.map((key, i) => {
    const level = FIDELITY_LEVELS[i];
    const nameApi = level?.nameKey ? t(level.nameKey) : null;
    return {
      key,
      percent: level?.percent || [5, 10, 15][i],
      name: nameApi && nameApi !== level.nameKey ? nameApi : tx(`hof.tiers.${key}.name`),
      signature: tx(`hof.tiers.${key}.signature`),
      threshold: tx(`hof.tiers.${key}.threshold`),
      ...HOF_TIER_STYLE[key],
    };
  });

  return (
    <section className="relative overflow-hidden border-t border-was-ink/8 bg-white">
      <div className={`relative ${SECTION}`}>
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between md:gap-10">
          <FadeUp>
            <Kicker n="03" scene={tx('sceneLabel')}>{tx('hof.kicker')}</Kicker>
            <h2 className="mt-5 max-w-[760px] text-balance text-[26px] font-heading font-extrabold uppercase tracking-[-0.01em] leading-[1.06] text-was-ink md:text-[34px]">{tx('hof.title')}</h2>
            <p className="mt-4 max-w-[560px] text-base leading-relaxed text-was-slate">{tx('hof.desc')}</p>
          </FadeUp>
          <a href={hofHref} className="mb-1.5 inline-flex shrink-0 items-center gap-2 self-start rounded-lg border border-was-ink/15 px-6 py-2.5 text-[15px] font-normal text-was-ink transition duration-200 hover:border-was-ink hover:text-was-ink md:self-auto">
            {tx('hof.more')}
            <ArrowIcon />
          </a>
        </div>
        <Stagger stagger={0.08} delay={0.1} className="mt-12 grid border-y border-was-ink/8 md:grid-cols-2 lg:mt-16 lg:grid-cols-4">
          {tiers.map((tier, i) => (
            <StaggerChild key={`homehof${tier.key}`} className={`flex flex-col py-8 md:px-8 ${i > 0 ? 'border-t border-was-ink/8 md:border-t-0 lg:border-l' : ''} ${i === 0 ? 'md:pl-0' : ''} ${i === 1 ? 'md:border-l' : ''}`}>
              <div className="flex items-center gap-3">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-was-ink text-white [&>svg]:size-5" aria-hidden="true">{tierIconsCompact[tier.key]}</span>
                <h3 className="m-0 font-heading text-[17px] font-extrabold uppercase leading-none tracking-[0.01em] text-was-ink">{tier.name}</h3>
              </div>
              <div className="mt-6 font-heading text-[40px] font-extrabold leading-none tracking-[-0.01em] text-was-ink">{`-${tier.percent}%`}</div>
              <div className="mt-3 text-[15px] text-was-ink">{tier.threshold}</div>
            </StaggerChild>
          ))}
          <StaggerChild className="flex flex-col border-t border-was-ink/8 py-8 md:border-l md:px-8 md:pr-0 lg:border-t-0">
            <div className="flex items-center gap-3">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-red text-white [&>svg]:size-5" aria-hidden="true">{tierIconsCompact.legend}</span>
              <h3 className="m-0 font-heading text-[17px] font-extrabold uppercase leading-none tracking-[0.01em] text-was-ink">{String(tx('hof.plusPrefix')).replace(/[,\s]+$/, '')}</h3>
            </div>
            <div className="mt-6 font-heading text-[40px] font-extrabold leading-none tracking-[-0.01em] text-was-ink">
              <span className="mr-2 align-middle font-display text-sm font-normal normal-case text-was-muted">{tx('hof.plusUpTo')}</span>
              -30%
            </div>
            <div className="mt-3 text-[15px] text-was-ink">{tx('hof.plusPrice')}</div>
            <ul className="m-0 mt-5 list-none space-y-1.5 border-t border-was-ink/8 p-0 pt-4 text-[14px] text-was-slate">
              {String(tx('hof.plusFoot')).split(' · ').map((item) => (
                <li key={`homeplus${item}`} className="first-letter:uppercase">{item}</li>
              ))}
            </ul>
          </StaggerChild>
        </Stagger>
      </div>
    </section>
  );
}

function UsagesSection({ tx }) {
  const words = tx('usages.words') || [];
  const items = tx('usages.items') || [];
  const wordIdx = useRotatingIndex(2600);

  return (
    <section id="usages" className="border-t border-was-ink/8 bg-was-cream">
      <div className={SECTION}>
        <FadeUp>
          <Kicker n="04" scene={tx('sceneLabel')}>{tx('usages.kicker')}</Kicker>
          <h2 className="mt-5 max-w-[900px] text-[26px] font-heading font-extrabold uppercase tracking-[-0.01em] leading-[1.06] md:text-[38px]">
            {tx('usages.titleLine1')}
            <br />
            {tx('usages.titleLine2')}
            {' '}
            <span className="whitespace-nowrap">
              {tx('usages.titleFor')}
              {' '}
              <span className="relative -my-[0.2em] inline-grid overflow-hidden py-[0.2em] align-bottom">
              <AnimatePresence mode="wait" initial={false}>
                <motion.em
                  key={words.length ? words[wordIdx % words.length] : 'w'}
                  initial={{ y: '0.6em', opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: '-0.6em', opacity: 0 }}
                  transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  className="col-start-1 row-start-1 whitespace-nowrap pr-[0.08em] not-italic text-was-violet"
                >
                  {words.length ? words[wordIdx % words.length] : ''}
                </motion.em>
              </AnimatePresence>
              </span>
            </span>
          </h2>
        </FadeUp>
        <Stagger stagger={0.06} delay={0.1} className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4 lg:mt-14 lg:gap-5">
          {items.map((u) => (
            <StaggerChild key={`homeusage${u.key}`} className="group relative flex aspect-[3/4] flex-col justify-end overflow-hidden rounded-xl bg-was-plum transition duration-300 ease-out hover:-translate-y-0.5">
              <div className="absolute inset-0 transition duration-700 ease-out group-hover:scale-105">
                <Image src={USAGE_IMAGES[u.key]} alt={u.title} fill sizes="(max-width: 768px) 50vw, 300px" className="object-cover" />
              </div>
              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(19,14,27,0)_45%,rgba(19,14,27,0.78)_100%)]" />
              <div className="pointer-events-none relative flex flex-col gap-1 p-5 lg:p-6">
                <h3 className="m-0 text-xl font-bold leading-[1.15] tracking-[-0.02em] text-white lg:text-[22px]">{u.title}</h3>
                <div className="text-[13px] text-white/75 lg:text-sm">{u.claim}</div>
              </div>
            </StaggerChild>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

// Icônes des piliers (tracés de la maquette) ; Scene Cover utilise son logo.
const PILLAR_ICONS = {
  ai: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9zM18.5 15.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z',
  booking: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6M9 13h6M9 17h4',
  scouting: 'M21 21l-4.3-4.3M11 3a8 8 0 1 0 0 16 8 8 0 0 0 0-16',
};

function PillarIcon({ id }) {
  if (id === 'cover') {
    return <img src="/img/home-v5/scene-cover-logo.png" alt="Scene Cover" className="static size-7 object-contain" loading="lazy" />;
  }
  const d = PILLAR_ICONS[id];
  const filled = id === 'ai';
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke={filled ? 'none' : 'currentColor'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

function PillarsSection({ tx }) {
  const items = tx('pillars.items') || [];
  return (
    <section id="piliers" className="border-t border-was-ink/8 bg-white">
      <div className={SECTION}>
        <FadeUp>
          <Kicker n="05" scene={tx('sceneLabel')}>{tx('pillars.kicker')}</Kicker>
          <h2 className="mt-5 text-[26px] font-heading font-extrabold uppercase tracking-[-0.01em] leading-[1.06] md:text-[38px]">
            {tx('pillars.titleLine1')}
            <br />
            {tx('pillars.titleLine2')}
          </h2>
        </FadeUp>
        <Stagger stagger={0.08} delay={0.1} className="mt-12 grid border-t border-was-ink/8 md:grid-cols-2 md:gap-x-16 lg:mt-16">
          {items.map((p, i) => (
            <StaggerChild key={`homepillar${p.key}`} className="relative grid grid-cols-[56px_1fr] gap-5 border-b border-was-ink/8 py-8 lg:py-10">
              <span className="absolute right-0 top-8 text-sm tabular-nums text-was-muted lg:top-10">{String(i + 1).padStart(2, '0')}</span>
              <span className="flex size-12 items-center justify-center rounded-xl bg-was-violet/10 text-was-violet" aria-hidden="true"><PillarIcon id={p.key} /></span>
              <div className="flex min-w-0 flex-col pr-10">
                <h3 className="m-0 text-[22px] font-bold leading-[1.15] tracking-[-0.02em]">{p.title}</h3>
                <p className="m-0 mt-1.5 text-[15px] font-medium text-was-violet">{p.claim}</p>
                <p className="m-0 mt-3 max-w-[440px] text-[15px] leading-relaxed text-was-slate">{p.desc}</p>
                {p.key === 'cover' && (
                  <p className="m-0 mt-3 flex items-center gap-2 text-[13px] text-was-muted">
                    {tx('pillars.partner')}
                    <img src="/img/axa-logo.svg" alt="AXA" className="static size-6 rounded-[4px]" loading="lazy" />
                  </p>
                )}
              </div>
            </StaggerChild>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

const WatchTestimonial = InstagramModal(({ openModalInstagram, label, aria }) => (
  <button type="button" onClick={openModalInstagram} aria-label={aria} className="group inline-flex shrink-0 items-center gap-2 text-[13px] text-was-ink transition-colors hover:text-was-violet">
    <span className="flex size-8 items-center justify-center rounded-full bg-was-ink text-white transition-colors group-hover:bg-was-violet"><PlayIcon size={12} fill="currentColor" /></span>
    {label}
  </button>
));

function TestimonialsSection({ tx }) {
  const featured = tx('testimonials.featured') || {};
  const side = tx('testimonials.side') || [];
  const items = [
    { ...featured, video: TESTIMONIAL_VIDEOS.featured, logo: FEATURED_LOGO },
    ...side.map((tItem, i) => ({ ...tItem, video: TESTIMONIAL_VIDEOS.side[i], logo: SIDE_LOGOS[i] })),
  ];
  return (
    <section className="border-t border-was-ink/8 bg-was-cream">
      <div className={SECTION}>
        <FadeUp>
          <Kicker n="06" scene={tx('sceneLabel')}>{tx('testimonials.kicker')}</Kicker>
          <h2 className="mt-5 max-w-[760px] text-balance text-[26px] font-heading font-extrabold uppercase tracking-[-0.01em] leading-[1.06] md:text-[34px]">{tx('testimonials.title')}</h2>
        </FadeUp>
        <Stagger stagger={0.08} delay={0.1} className="mt-12 grid border-y border-was-ink/8 md:grid-cols-3 lg:mt-16">
          {items.map((tItem, i) => (
            <StaggerChild as="figure" key={`hometestimonial${tItem.name}`} className={`m-0 flex flex-col py-8 md:px-8 lg:py-10 ${i > 0 ? 'border-t border-was-ink/8 md:border-l md:border-t-0' : 'md:pl-0'} ${i === items.length - 1 ? 'md:pr-0' : ''}`}>
              <div className="flex h-11 items-center">
                <img src={tItem.logo?.src} alt={tItem.logo?.alt} className={`static ${tItem.logo?.className || 'h-7'} w-auto max-w-[200px] object-contain object-left`} loading="lazy" />
              </div>
              <blockquote className="m-0 mt-7 flex-1 text-[20px] font-light leading-[1.4] tracking-[-0.01em] text-was-ink lg:text-[22px]">
                «
                {' '}
                {tItem.quote}
                {' '}
                »
              </blockquote>
              <figcaption className="mt-8 flex items-end justify-between gap-4 border-t border-was-ink/8 pt-5">
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-[15px] font-bold">{tItem.name}</span>
                  <span className="text-[13px] text-was-muted">{tItem.role}</span>
                </span>
                <WatchTestimonial postUrl={tItem.video} label={tx('testimonials.watch')} aria={tx('testimonials.watchAria')} />
              </figcaption>
            </StaggerChild>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function CtaSection({ tx, searchHref, briefHref }) {
  return (
    <section className="was-grain relative overflow-hidden bg-was-abyss">
      <div className="absolute inset-0">
        <Image src="/img/home-v5/respiration-bts.jpg" alt="" fill sizes="100vw" className="object-cover object-center" />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(10,7,16,0.55)_0%,rgba(10,7,16,0.7)_60%,rgba(10,7,16,0.85)_100%)]" />
      <div className={`relative ${SECTION} lg:py-40`}>
        <FadeUp>
          <h2 className="m-0 max-w-[900px] text-balance text-[28px] font-heading font-extrabold uppercase tracking-[-0.01em] leading-[1.04] text-white md:text-[44px]">{tx('cta.title')}</h2>
          <div className="mt-8 flex flex-wrap gap-4 lg:mt-9">
            <a href={searchHref} className="inline-flex items-center gap-2 rounded-lg bg-white px-7 py-3.5 text-base font-normal text-was-ink transition duration-200 hover:bg-was-cream hover:text-was-ink">
              {tx('cta.search')}
              <SearchIcon />
            </a>
            <a href={briefHref} className="inline-flex items-center gap-2 rounded-lg border border-white/30 px-7 py-3.5 text-base font-normal text-white transition duration-200 hover:border-white hover:text-white">
              {tx('cta.brief')}
            </a>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

export default function HomePage(props) {
  const { spaces } = props;
  const { t } = useTranslation();
  const { tx, lang: langCurrent } = useHomeCopy();
  const site = process.env.NEXT_PUBLIC_URL_V4;
  const canURL = `${site}${getRootUrl(langCurrent)}`;
  const searchHref = `${getRootUrl(langCurrent)}/s/paris`;
  const briefHref = getPathByLang('brief', langCurrent);
  const hofHref = getPathByLang('hall-of-fame', langCurrent);

  return (
    <>
      <Head>
        <title>{t('home:meta_title')}</title>
        <meta name="description" content={t('home:meta_desc')} />
        <link rel="canonical" href={canURL} />
        <link rel="alternate" hrefLang="x-default" href={`${site}/fr`} />
        <link rel="alternate" hrefLang="en" href={`${site}`} />
        <link rel="alternate" hrefLang="fr" href={`${site}/fr`} />
        <link rel="alternate" hrefLang="es" href={`${site}/es`} />
      </Head>
      <div itemScope itemType="http://schema.org/Organization">
        <meta itemProp="name" content="We Are Scene" />
        <meta itemProp="legalName" content="OfficeRiders" />
        <meta itemProp="logo" content={`${site}/img/logo-or.png`} />
        <meta itemProp="url" content={`${site}`} />
      </div>
      <div className="hidden" itemScope itemType="https://schema.org/WebSite">
        <meta itemProp="url" content={`${site}`} />
        <meta itemProp="name" content="We Are Scene" />
        <meta itemProp="legalName" content="OfficeRiders" />
        <form itemProp="potentialAction" itemScope itemType="https://schema.org/SearchAction">
          <meta itemProp="target" content={`${site}/s/paris?&pattern={search_term_string}`} />
          <input itemProp="query-input" type="text" name="search_term_string" required />
          <input type="submit" />
        </form>
      </div>
      <div className="bg-was-cream font-display text-was-ink antialiased">
        <HeroSection tx={tx} />
        <ProofBand count={tx('proof.count')} label={tx('proof.label')} />
        <SelectionSection tx={tx} spaces={spaces} searchHref={searchHref} />
        <BriefSection tx={tx} briefHref={briefHref} />
        <HallOfFameSection tx={tx} t={t} hofHref={hofHref} />
        <UsagesSection tx={tx} />
        <PillarsSection tx={tx} />
        <TestimonialsSection tx={tx} />
        <section id="hote" className="bg-was-cream">
          <div className="mx-auto max-w-[1240px] px-6 pb-20 lg:px-8 lg:pb-28">
            <FadeUp>
              <HostSimulator tx={tx} lang={langCurrent} onBecomeHost={gotoAddSpace} videoId={HOST_VIDEO_ID} />
            </FadeUp>
          </div>
        </section>
        <CtaSection tx={tx} searchHref={searchHref} briefHref={briefHref} />
      </div>
    </>
  );
}
